import {useQueryClient} from '@tanstack/react-query'
import TextArea from 'antd/es/input/TextArea'
import ReactECharts from 'echarts-for-react'
import {Button} from 'primereact/button'
import {Message} from 'primereact/message'
import {Rating} from 'primereact/rating'
import {SelectButton} from 'primereact/selectbutton'
import React, {useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom'
import {ReactComponent as BiSolidDislike} from '../../assets/icons/BiSolidDislike.svg'
import {ReactComponent as BiSolidLike} from '../../assets/icons/BiSolidLike.svg'
import {ReactComponent as BsFillHeartPulseFill} from '../../assets/icons/BsFillHeartPulseFill.svg'
import {ReactComponent as FiActivity} from '../../assets/icons/FiActivity.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {useAuth} from '../authentication/AuthProvider'
import FlexBox from '../layout/FlexBox'
import {useParameterByUser} from '../parameter/query-keys/ParameterKeysManager'
import {ECGDeviceTypes, signalOptions} from './ECGConstants'
import ECGHistoryTable from './ECGHistoryTable'
import './ecg.css'
import {useElectrocardiogramCRUD, useUserECG} from './query-keys/ECGKeysManager'

//TODO translation here
// const QueryKey = 'Electrocardiogram'
const PATH = 'ecg'

const ECGWrapper = ({type = ECGDeviceTypes.samsung}) => {
    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }

        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Translate
    const {translateKeys, translateOptions, translateCommons} = TranslateUtils()
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const ecgQueryKey = useMemo(() => {
        const {ecgKeys} = QueryKeyManager(QueryKeyTypes.ecg, user?.id)

        return ecgKeys.detailByUser({userId: id, brand: type})
    }, [id, type, user?.id])

    const {data: ecgData, refetch} = useUserECG(
        user?.id,
        id,
        type,
        !!user?.id && !!id && !!type,
    )

    const {data: parameterData} = useParameterByUser(
        user?.id,
        id,
        !!user?.id && !!id && type === ECGDeviceTypes.spatch,
    )

    const {mutateAsync: createOrUpdateECG} = useElectrocardiogramCRUD(
        !!ecgData?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Ref
    const ecgHistoryTableRef = useRef(null)
    //#endregion

    //#region Handle Function
    const handleUpdateECGData = async () => {
        let toPostData = {
            ...ecgData,
            data: ecgData?.data ? JSON.stringify(ecgData?.data) : '[]',
            additionalData: ecgData?.additionalData
                ? JSON.stringify(ecgData?.additionalData)
                : '[]',
        }

        const response = await createOrUpdateECG(toPostData)

        if (response?.id) {
            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: ecgQueryKey,
            })
        }
    }
    //#endregion

    //#region Memo
    const ecgDataset = useMemo(() => {
        if (!ecgData || !ecgData?.data) return []

        return ecgData?.data
    }, [ecgData])

    const boundary = useMemo(() => {
        if (!ecgDataset || ecgDataset?.length === 0) return {}

        let temp = {}

        const max = Math.max(...ecgDataset)
        const min = Math.min(...ecgDataset)

        temp['max'] = max
        temp['min'] = min

        return temp
    }, [ecgDataset])

    const ecgOption = useMemo(() => {
        if (!ecgDataset || ecgDataset?.length === 0) return {}

        return {
            animation: true,
            grid: {
                top: 40,
                left: 50,
                right: 20,
                bottom: 50,
            },
            dataZoom: [
                {
                    show: true,
                    type: 'inside',
                    filterMode: 'none',
                    xAxisIndex: [0],
                    startValue: 0,
                    endValue: ecgDataset?.length,
                },
                {
                    show: true,
                    type: 'inside',
                    filterMode: 'none',
                    yAxisIndex: [0],
                },
                {
                    type: 'slider',
                    filterMode: 'filter',
                    show: true,
                    xAxisIndex: [0],
                    startValue: 1000,
                    endValue: 1000,
                    backgroundColor: 'rgba(68, 184, 166, 0.1)',
                    dataBackground: {
                        lineStyle: 'black',
                    },
                    fillerColor: 'rgba(68, 184, 166, 0.1)',
                    borderColor: 'rgba(68, 184, 166, 0.1)',
                    moveHandleStyle: {
                        color: 'darkcyan',
                        shadowColor: 'rgba(68, 184, 166, 0.1)',
                    },
                    showDetail: true,
                    realtime: true,
                    textStyle: {
                        fontWeight: 'bold',
                    },
                },
                {
                    show: false,
                    type: 'inside',
                    yAxisIndex: [0],
                },
            ],
            xAxis: {
                type: 'category',
                data: ecgDataset?.map((_, index) => index),
                minorTick: {
                    show: true,
                },
                minorSplitLine: {
                    show: true,
                },
            },
            yAxis: {
                type: 'value',
                minorTick: {
                    show: true,
                },
                minorSplitLine: {
                    show: true,
                },
            },
            series: [
                {
                    data: ecgDataset?.map(value => {
                        if (type === ECGDeviceTypes.samsung) {
                            return value - (boundary?.min + 20 || 180)
                        }

                        return value
                    }),
                    type: 'line',
                    smooth: true,
                    lineStyle: {
                        color: 'darkcyan',
                    },
                    showSymbol: false,
                    clip: false,
                },
            ],
        }
    }, [ecgData, boundary])

    const heartRateDataset = useMemo(() => {
        if (
            (type !== ECGDeviceTypes.spatch && type !== ECGDeviceTypes.apple) ||
            !ecgData ||
            !ecgData?.additionalData
        )
            return []

        return ecgData?.additionalData
    }, [ecgData])

    const heartRateAvg = useMemo(() => {
        if (!heartRateDataset || heartRateDataset?.length === 0) return 0

        const avgHeartRate =
            heartRateDataset?.reduce((acc, cur) => acc + cur, 0) /
            heartRateDataset?.length

        return parseInt(avgHeartRate)
    }, [heartRateDataset])

    const heartRateOption = useMemo(() => {
        return {
            animation: true,
            title: {},
            xAxis: {
                type: 'category',
                data: [],
            },
            yAxis: {
                type: 'value',
            },
            xAxis: {
                type: 'category',
                data: heartRateDataset?.map((_, index) => index),
                minorTick: {
                    show: true,
                },
                minorSplitLine: {
                    show: true,
                },
            },
            tooltip: {
                show: true,
                trigger: 'axis',

                formatter: `${translateKeys('Heart Rate')}: {c}`,
            },
            series: [
                {
                    data: heartRateDataset,
                    type: 'line',
                    lineStyle: {
                        color: 'darkcyan',
                    },
                    label: {
                        position: 'top',
                        show: true,
                    },
                },
            ],
        }
    }, [heartRateDataset, translateKeys])

    const ratingValue = useMemo(() => {
        if (!!ecgData?.rating && ecgData.rating !== 'unrated') {
            if (ecgData.rating === 'one_star') return 1
            else if (ecgData.rating === 'two_stars') return 2
            else if (ecgData.rating === 'three_stars') return 3
            else if (ecgData.rating === 'four_stars') return 4
            else if (ecgData.rating === 'five_stars') return 5
        } else {
            return 0
        }
    }, [ecgData])
    //#endregion

    //#region Render
    const ratingToPostValue = value => {
        if (!value || value === 0) {
            return 0
        } else {
            if (value === 1) {
                return 'one_star'
            } else if (value === 2) {
                return 'two_stars'
            } else if (value === 3) {
                return 'three_stars'
            } else if (value === 4) {
                return 'four_stars'
            } else if (value === 5) {
                return 'five_stars'
            }
        }
    }

    const signalTemplate = option => {
        return (
            <FlexBox className={'flex-column'}>
                <div>
                    {option.value === 'like' ? (
                        <BiSolidLike
                            style={{
                                width: '24px',
                                height: '24px',
                                fill: `${
                                    ecgData?.signal === option.value
                                        ? 'white'
                                        : ''
                                }`,
                            }}
                        />
                    ) : (
                        <BiSolidDislike
                            style={{
                                width: '24px',
                                height: '24px',
                                fill: `${
                                    ecgData?.signal === option.value
                                        ? 'white'
                                        : ''
                                }`,
                            }}
                        />
                    )}
                </div>
            </FlexBox>
        )
    }
    //#endregion

    //#region Event Listener
    // useEffect(() => {
    //     if (!!id) {
    //         detailEvent.on(
    //             'DETAIL-INFORMATION-RELOAD-' + ecgQueryKey + id,
    //             refetch,
    //         )

    //         return () => {
    //             detailEvent.removeListener(
    //                 'DETAIL-INFORMATION-RELOAD-' + ecgQueryKey + id,
    //             )
    //         }
    //     }
    // }, [ecgQueryKey, id, refetch])
    //#endregion

    return (
        <React.Fragment>
            <div className="col-12 p-0">
                <div className="flex my-3 border-round justify-content-between align-items-center py-3 px-2 flex-wrap bg-teal-50 ">
                    <h5
                        className={`pl-2 text-darkcyan font-bold m-0 py-2 text-center md:text-left`}>
                        <span className="mr-3">
                            <FiActivity
                                className="font-bold text-xl"
                                style={{width: '20px', height: '20px'}}
                            />
                        </span>
                        {translateKeys('Electrocardiogram (ECG)')}{' '}
                        {type === ECGDeviceTypes.spatch
                            ? ` - ${translateKeys('Heart Rate')}`
                            : ''}
                    </h5>
                    <Button
                        className="p-button-text gap-1 audit-button"
                        label={translateCommons('ECG History')}
                        icon="pi pi-history"
                        onClick={() =>
                            ecgHistoryTableRef.current.showForm(type)
                        }
                    />
                </div>

                {ecgDataset?.length > 0 ? (
                    <React.Fragment>
                        {type === ECGDeviceTypes.spatch ||
                            (type === ECGDeviceTypes.apple && (
                                <React.Fragment>
                                    <div className="grid">
                                        <div className="md:col-4 col-12">
                                            <div className="flex justify-content-center align-items-center mt-8 mb-5">
                                                <div
                                                    className="flex justify-content-center align-items-center"
                                                    style={{
                                                        width: '300px',
                                                        height: '300px',
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            'rgba(68, 184, 166, 0.8)',
                                                    }}>
                                                    <div
                                                        className="flex justify-content-center align-items-center"
                                                        style={{
                                                            width: '260px',
                                                            height: '260px',
                                                            borderRadius: '50%',
                                                            backgroundColor:
                                                                'rgba(68, 184, 166, 0.6)',
                                                        }}>
                                                        <div
                                                            className="flex justify-content-center align-items-center"
                                                            style={{
                                                                width: '220px',
                                                                height: '220px',
                                                                borderRadius:
                                                                    '50%',
                                                                backgroundColor:
                                                                    'rgba(0, 0, 0, 0.1)',
                                                            }}>
                                                            <div className="flex justify-content-center align-items-center gap-2">
                                                                <h1
                                                                    className="mb-0"
                                                                    style={{
                                                                        fontWeight:
                                                                            'bold',
                                                                        color: 'white',
                                                                        fontSize:
                                                                            '48px',
                                                                    }}>
                                                                    {
                                                                        heartRateAvg
                                                                    }
                                                                </h1>
                                                                <div className="flex-column justify-content-center align-items-center">
                                                                    <div>
                                                                        {' '}
                                                                        {translateKeys(
                                                                            'BPM',
                                                                        )}
                                                                    </div>
                                                                    <div className="flex justify-content-center align-items-center">
                                                                        <BsFillHeartPulseFill
                                                                            color="red"
                                                                            className="mr-2"
                                                                            style={{
                                                                                width: '24px',
                                                                                height: '24px',
                                                                            }}
                                                                        />
                                                                        <div
                                                                            style={{
                                                                                color: 'white',
                                                                                fontWeight:
                                                                                    'bold',
                                                                            }}>
                                                                            {heartRateAvg >
                                                                            parameterData?.heartRateUpper
                                                                                ? translateKeys(
                                                                                      'High',
                                                                                  )
                                                                                : heartRateAvg <
                                                                                  parameterData?.heartRateLower
                                                                                ? translateKeys(
                                                                                      'Low',
                                                                                  )
                                                                                : translateKeys(
                                                                                      'Normal',
                                                                                  )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <h4 className="font-bold">
                                                    {translateKeys(
                                                        'Heart Rate',
                                                    )}
                                                </h4>
                                                <p>
                                                    {translateKeys(
                                                        'The average heart rate is calculated from the heart rate data recorded during the ECG measurement.',
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="md:col-8 col-12">
                                            <div>
                                                <ReactECharts
                                                    style={{height: '50vh'}}
                                                    option={heartRateOption}
                                                />
                                            </div>
                                            <p className="text-center">
                                                {translateKeys(
                                                    'The chart shows the heart rate recorded during measuring the ECG',
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </React.Fragment>
                            ))}

                        <ReactECharts
                            style={{minHeight: '50vh'}}
                            option={ecgOption}
                        />
                        <p className="text-center mb-5">
                            {translateKeys(
                                'The chart shows the ECG data recorded during the measurement',
                            )}
                        </p>

                        <div className="grid p-0">
                            <div className="md:col-3 col-12 px-0">
                                <p className="mb-2 font-semibold ">
                                    {translateCommons(
                                        'How is the signal quality?',
                                    )}
                                </p>
                                <SelectButton
                                    value={ecgData?.signal}
                                    optionLabel="label"
                                    optionValue="value"
                                    options={translateOptions(signalOptions)}
                                    itemTemplate={signalTemplate}
                                    onChange={e => {
                                        queryClient.setQueryData(
                                            ecgQueryKey,
                                            oldData => {
                                                if (
                                                    !!oldData &&
                                                    Object.keys(oldData)
                                                        .length > 0
                                                ) {
                                                    if (e?.value) {
                                                        return {
                                                            ...oldData,
                                                            signal: e.value,
                                                        }
                                                    } else {
                                                        return {
                                                            ...oldData,
                                                            signal: 'none',
                                                            rating: 'unrated',
                                                        }
                                                    }
                                                }
                                            },
                                        )
                                    }}
                                />
                            </div>
                            <div className="md:col-3 col-12  px-0">
                                <p className="mb-2 font-semibold ">
                                    {translateCommons(
                                        'How would you rate this signal?',
                                    )}
                                </p>

                                <Rating
                                    disabled={
                                        !ecgData?.signal ||
                                        ecgData?.signal === 'none'
                                    }
                                    style={{
                                        fontSize: '24px',
                                        height: '40px',
                                    }}
                                    value={ratingValue}
                                    cancelIcon={'pi pi-trash'}
                                    onChange={e => {
                                        const ratingPostValue =
                                            ratingToPostValue(e.value)

                                        queryClient.setQueryData(
                                            ecgQueryKey,
                                            oldData => {
                                                if (
                                                    !!oldData &&
                                                    Object.keys(oldData)
                                                        .length > 0
                                                ) {
                                                    return {
                                                        ...oldData,
                                                        rating: ratingPostValue,
                                                    }
                                                }
                                            },
                                        )
                                    }}
                                />
                            </div>
                        </div>

                        <div className="w-full">
                            <TextArea
                                className="col-12 px-3 mt-2"
                                value={ecgData?.comment || ''}
                                placeholder={translateKeys(
                                    'Please enter your comment here...',
                                )}
                                autoSize={{minRows: 5, maxRows: 10}}
                                onChange={e => {
                                    queryClient.setQueryData(
                                        ecgQueryKey,
                                        oldData => {
                                            if (
                                                !!oldData &&
                                                Object.keys(oldData).length > 0
                                            ) {
                                                return {
                                                    ...oldData,
                                                    comment: e.target.value,
                                                }
                                            }
                                        },
                                    )
                                }}
                            />
                        </div>

                        <Button
                            label={translateKeys('SUBMIT')}
                            icon="pi pi-send"
                            className="p-button-primary col-12 p-button-lg font-bold"
                            style={{marginTop: '10px', height: '50px'}}
                            onClick={handleUpdateECGData}
                        />
                    </React.Fragment>
                ) : (
                    <Message
                        className="col-12"
                        severity="info"
                        text={translateKeys('No ECG data')}
                        style={{
                            height: '50px',
                        }}
                    />
                )}
            </div>
            <ECGHistoryTable ref={ecgHistoryTableRef} />
        </React.Fragment>
    )
}

export default ECGWrapper
